import Dashboard from '@pages/Dashboard';
import CrudPage from '@pages/CrudPage';
import Scraper from '@pages/Scraper';
import ScraperJob from '@pages/ScraperJob';
import Videos from '@pages/Videos';
import VideoDetail from '@pages/VideoDetail';
import Settings from '@pages/Settings';
import VideoCompress from '@pages/tools/VideoCompress';
import DbMigration from '@pages/tools/DbMigration';
import DataMigration from '@pages/tools/DataMigration';
import DataMigrationTableDiff from '@pages/tools/DataMigrationTableDiff';
import Github from '@pages/github/Github';
import GithubCallback from '@pages/github/Callback';
import GitApps from '@pages/development/GitApps';
import GitAppWorkspace from '@pages/development/workspace';

const kebab = (s) =>
  String(s)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();

/**
 * Models that have a purpose-built screen and must NOT also get a generic CRUD page.
 * Keyed "module:Model" so two modules can't collide on a shared model name.
 */
const BESPOKE_MODELS = new Set([
  'scraper:ScrapeJob', 'scraper:ScrapePage',
  'video:Video', 'video:VideoView',
  // The migration run/step tables are driven entirely by the two migration screens —
  // a raw CRUD table over them is noise, and editing a run row by hand would desync the
  // audit trail from what actually ran. UtilityJob is the same: /tools/video-compress owns
  // it, and the model refuses both create and update through the generic door anyway.
  'tools:UtilityJob',
  'tools:DataMigrationRun', 'tools:DataMigrationStep',
  'tools:DbMigrationRun', 'tools:DbMigrationStep',
  // The connection row is managed entirely by /github; a generic table over it would
  // put a credential record in the nav and offer edit controls the model refuses.
  'github:GithubConnection',
  // Every development model is driven by /git-apps and its workspace tabs. A generic
  // CRUD page over app_tasks or the staged-changes table would expose the worker's
  // internal state as if it were editable.
  'development:GitApp', 'development:GitAppChange',
  'development:GitAppTicket', 'development:BackgroundJob',
]);

/**
 * Build the protected route table from the module registry (pass the live
 * registry from useModules()). Each model gets a generic list page; nav items +
 * route guards key off (module, action='view').
 *
 * Bespoke screens are declared explicitly after the generated ones — the generator only
 * knows how to build a generic table, which is the wrong shape for anything with its own
 * workflow. `nav: false` keeps a route reachable without a sidebar entry (a detail page
 * shouldn't appear in nav).
 */
export function buildRoutes(modules = {}) {
  const routes = [
    { path: '/', element: <Dashboard />, label: 'Dashboard', icon: 'layout-dashboard', module: 'core', action: 'view' },
    // Gated on manage_settings, not view: the page discloses which integrations are configured
    // and every write on it needs that capability anyway, so a viewer would only see 403s.
    { path: '/settings', element: <Settings />, label: 'Settings', icon: 'settings', module: 'core', action: 'manage_settings' },
  ];

  for (const mod of Object.values(modules)) {
    for (const model of mod.models || []) {
      if (BESPOKE_MODELS.has(`${mod.key}:${model}`)) continue;
      routes.push({
        path: `/${kebab(model)}`,
        element: <CrudPage model={model} module={mod.key} title={model} />,
        label: model,
        icon: mod.icon,
        module: mod.key,
        action: 'view',
      });
    }
  }

  if (modules.scraper) {
    routes.push(
      {
        path: '/scraper',
        element: <Scraper />,
        label: modules.scraper.label || 'Web Scraper',
        icon: modules.scraper.icon || 'globe',
        module: 'scraper',
        action: 'view',
      },
      {
        path: '/scraper/:id',
        element: <ScraperJob />,
        label: 'Crawl detail',
        icon: modules.scraper.icon || 'globe',
        module: 'scraper',
        action: 'view',
        nav: false,
      },
    );
  }

  if (modules.video) {
    routes.push(
      {
        path: '/videos',
        element: <Videos />,
        label: modules.video.label || 'Videos',
        icon: modules.video.icon || 'video',
        module: 'video',
        action: 'view',
      },
      {
        path: '/videos/:id',
        element: <VideoDetail />,
        label: 'Video detail',
        icon: modules.video.icon || 'video',
        module: 'video',
        action: 'view',
        nav: false,
      },
    );
  }

  if (modules.tools) {
    const icon = modules.tools.icon || 'wrench';
    // `manage`, not `view`: every /api/utility/*-migration route checks tools:manage, so
    // guarding these screens on `view` would render a UI whose every call 403s.
    routes.push(
      {
        // `view`, not `create`: polling a job and downloading its output only need view, and
        // the page itself gates the Compress button on tools:create. Guarding the whole route
        // on `create` would hide finished jobs from someone allowed to read them.
        path: '/tools/video-compress',
        element: <VideoCompress />,
        label: 'Compress Video',
        icon,
        module: 'tools',
        action: 'view',
      },
      {
        path: '/tools/data-migration',
        element: <DataMigration />,
        label: 'Data Migration',
        icon,
        module: 'tools',
        action: 'manage',
      },
      {
        path: '/tools/data-migration/:runId/:table',
        element: <DataMigrationTableDiff />,
        label: 'Table diff',
        icon,
        module: 'tools',
        action: 'manage',
        nav: false,
      },
      {
        path: '/tools/db-migration',
        element: <DbMigration />,
        label: 'DB Migration',
        icon,
        module: 'tools',
        action: 'manage',
      },
    );
  }

  if (modules.github) {
    const icon = modules.github.icon || 'github';
    routes.push(
      {
        path: '/github',
        element: <Github />,
        label: modules.github.label || 'GitHub',
        icon,
        module: 'github',
        action: 'view',
      },
      {
        // GitHub redirects a real browser here, so it stays inside the authenticated
        // shell — the code is exchanged against the caller's own session. `view` rather
        // than `connect`: a user who can see the page but lacks connect gets a clean
        // error from the action instead of a bare /unauthorized redirect mid-OAuth.
        path: '/github/callback',
        element: <GithubCallback />,
        label: 'GitHub callback',
        icon,
        module: 'github',
        action: 'view',
        nav: false,
      },
    );
  }

  if (modules.development) {
    const icon = modules.development.icon || 'code';
    routes.push(
      {
        path: '/git-apps',
        element: <GitApps />,
        label: modules.development.label || 'Development',
        icon,
        module: 'development',
        action: 'view',
      },
      {
        path: '/git-apps/:id',
        element: <GitAppWorkspace />,
        label: 'Workspace',
        icon,
        module: 'development',
        action: 'view',
        nav: false,
      },
    );
  }

  // NOTE: /embed/:publicId is deliberately NOT here. Every route this function returns is
  // wrapped in RequireAuth + DefaultLayout by App.jsx, and the embed player must be
  // public and chrome-less. It is declared as a top-level public route in App.jsx instead.

  return routes;
}
